import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { IPagination } from '@shared/interfaces/pagination.interface';
import { PaginationService } from '@shared/services/pagination/pagination.service';
import { Subscription } from 'rxjs';

import '@shared/components/pagination/ngx-bootstrap-pagination-init.patch';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PaginationComponent implements OnInit, OnDestroy {
  // How many numbered page links the strip shows at once. The "Page _ of N"
  // jump field only appears once there are more pages than this, i.e. when
  // some page numbers are hidden and reaching them means hopping through the
  // strip. Up to this many pages, every page is already one click away.
  public static readonly MAX_SIZE = 5;

  private static nextId = 0;

  @Input() public pagination: PaginationService<any>;
  public totalEntries: number;
  public totalPages: number;
  // Mirrors the current page into the jump field whenever the page changes
  // through the strip (or the API response), so the field never shows a
  // stale number.
  public jumpPage: number;

  public readonly maxSize = PaginationComponent.MAX_SIZE;
  public readonly jumpInputId = `pagination-jump-${PaginationComponent.nextId++}`;

  private readonly subscriptions = new Subscription();

  constructor() {}

  ngOnInit(): void {
    this.totalEntries = 0;
    this.totalPages = 0;
    this.jumpPage = this.pagination.currentPage;

    this.subscriptions.add(
      this.pagination.entries.subscribe((totalEntries: number) => {
        this.totalEntries = totalEntries;
        this.totalPages = Math.ceil(totalEntries / this.pagination.pageSize);
      })
    );

    this.subscriptions.add(
      this.pagination.page.subscribe((page: IPagination) => {
        this.jumpPage = page.page;
      })
    );

    this.pagination.checkAndLoadPage(this.pagination.currentPage);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public get showJump(): boolean {
    return this.totalPages > this.maxSize;
  }

  public onPageChange(event: any) {
    this.pagination.checkAndLoadPage(event.page);
  }

  // Enter or blur on the jump field. Out-of-range numbers are clamped
  // (typing 999 lands on the last page) and anything that is not a number
  // snaps back to the current page, so the field is never left showing
  // something the list is not.
  public onJump(input: HTMLInputElement): void {
    const requested = parseInt(input.value, 10);
    const current = this.pagination.currentPage;

    const target = isNaN(requested)
      ? current
      : Math.min(Math.max(requested, 1), this.totalPages);

    this.jumpPage = target;
    input.value = String(target);

    if (target === current) {
      return;
    }

    this.pagination.currentPage = target;
    this.pagination.checkAndLoadPage(target);
  }
}
